<template>
  <vf-button
    id="pdp-add-to-cart"
    :aria-disabled="pending || loading"
    aria-live="polite"
    :class="{ 'c-white': isSignInToBuy && signInToBuyTheme }"
    :class-content="{ 'w-full between': includePrice && isBuyable }"
    :disabled="(isComingSoon || isOutOfStock) && !isNotifyMe"
    :loading
    :size
    :theme="isSignInToBuy ? signInToBuyTheme : undefined"
    :type="pending ? 'button' : 'submit'"
    :variant="buttonVariant"
    @click="handleClick"
  >
    {{ label }}
    <product-pricing
      v-if="product && includePrice && isBuyable"
      class="pl-1"
      :currency="product.currency"
      :hide-strikethrough-price
      :price="price || product.price"
    />
  </vf-button>
</template>

<script lang="ts" setup>
import type { AttributeSelection, PriceFull, ProductExtended, SelectedVariant } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const {
  product,
  addToCartLabel,
  attributeSelection = {},
  enableColorAsAttribute,
  forceSignInToBuy,
  includeComingSoon,
  includeNotifyMe,
  includeReserveInStore,
  includeSelectSize,
  includeUpdateInCart,
  loading,
  pending,
  notifyMeLabel,
  price,
  isSticky
} = defineProps<{
  product: ProductExtended | null
  addToCartLabel?: string
  attributeSelection?: AttributeSelection
  enableColorAsAttribute?: boolean
  forceSignInToBuy?: boolean
  hideStrikethroughPrice?: boolean
  includeComingSoon?: boolean
  includeNotifyMe?: boolean
  includePrice?: boolean
  includeReserveInStore?: boolean
  includeSelectSize?: boolean
  includeUpdateInCart?: boolean
  loading?: boolean
  pending?: boolean
  notifyMeLabel?: string
  price?: PriceFull | null
  isSticky?: boolean
}>()

const emit = defineEmits<{
  'add-to-cart': []
  'notify-me': []
  'reserve-in-store': []
  'update-in-cart': []
}>()

const { signInToBuyTheme, size } = useAppConfig().components.product.cta
const auth = useAuthStore()
const { getComingSoonText, getIsComingSoonProduct } = useComingSoon()
const { $feature, $t } = useNuxtApp()
const user = useUserStore()

const isAnyVariantNotifyMe = computed(() => product?.variants.some(({ notifyMe }) => !!notifyMe))

const isNonLoyaltyMember = computed(() => auth.loggedIn && !user.isLoyaltyMember)

const isAttributeSelectionRequired = computed(() =>
  Object.keys(attributeSelection).length >= (enableColorAsAttribute ? 2 : 1)
)

const isVariantSelected = computed(
  () => isAttributeSelectionRequired.value
    && product?.attributes.every(({ type }) => attributeSelection[type] || type === 'color')
)

const selectedVariant = computed(() => {
  const variant = product?.variants.find((item) => Object.entries(attributeSelection).every(
    ([key, value]) => !item.attributes[key] || item.attributes[key] === value || key === 'color'
  )
  )
  return (variant && isVariantSelected.value ? variant : null) as SelectedVariant
})

const isComingSoon = computed(() => includeComingSoon && getIsComingSoonProduct(product?.badge, product?.eyebrow))

const isOutOfStock = computed(
  () => !loading && (!product || product.allVariantsOutOfStock || (isVariantSelected.value && selectedVariant.value?.productInventoryState !== 'InStock'))
)

const isNotifyMe = computed(
  () => includeNotifyMe
    && (isComingSoon.value || isOutOfStock.value)
    && (selectedVariant.value?.notifyMe || (isAnyVariantNotifyMe.value && !isVariantSelected.value))
    && !loading
)

const isRobisOnly = computed(() => includeReserveInStore && product?.isRobisOnly)

const isSignInToBuy = computed(() =>
  (forceSignInToBuy && !auth.loggedIn)
  || (!!product?.presale && (!auth.loggedIn || isNonLoyaltyMember.value)))

const isSoldOut = computed(() => isVariantSelected.value && selectedVariant.value?.productInventoryState === 'SoldOut')

const isBuyable = computed(() => !(isComingSoon.value || isOutOfStock.value || isRobisOnly.value || isSoldOut.value))

const isSelectSize = computed(() => isSticky && includeSelectSize && !selectedVariant.value && isBuyable.value)

const buttonVariant = computed(() => {
  if (isSignInToBuy.value && signInToBuyTheme) return 'themable'
  if (isSelectSize.value) return 'secondary'
  return 'primary'
})

// The price only renders while isBuyable (see template), which is exactly when the button
// is primary/themable (dark fill) - so the default original/sale price colors (tuned for a
// light background) always need this light override.
provide(RecommendationStyleContextKey, {
  originalPriceColor: { sm: '#ffffff' },
  salePriceColor: { sm: '#ff4b5f' },
})

const label = computed(() => {
  if (isBuyable.value) {
    if (isSignInToBuy.value && isNonLoyaltyMember.value) return $t.enrollInLoyaltyToBuy
    if (isSignInToBuy.value) return $t.signInToBuy
    if (isSelectSize.value) return $t.selectAttribute.size
    if (includeUpdateInCart) return $t.updateInCart
    if (addToCartLabel) return addToCartLabel
    return $t.addToCart
  }
  else {
    if (isNotifyMe.value) return notifyMeLabel || $t.notifyMe
    if (isComingSoon.value)
      return getComingSoonText($feature.showComingSoonDateInProductCta ? product?.targetPublishDate : '')
    if (isRobisOnly.value) return $t.reserveInStore
    if (isSoldOut.value) return $t.soldOut
    return $t.outOfStock
  }
})

const handleClick = () => {
  if (pending || loading) return
  if (isBuyable.value && includeUpdateInCart && !isSignInToBuy.value) return emit('update-in-cart')
  if (isBuyable.value) return emit('add-to-cart')
  if (isNotifyMe.value) return emit('notify-me')
  if (isRobisOnly.value) return emit('reserve-in-store')
}
</script>
